import type { BufferGeometry } from 'three';
import type { PolygonWithHoles, Vec2, Vec3 } from '../../geo/types';
import type { PaletteModel } from '../../data/types';
import type { ChunkSink } from '../mesh/chunker';
import type { NearDetailSet } from '../mesh/nearDetailLod';
import {
  createMeshBuilder,
  type Frame,
  type MeshBuilder,
  type VertexStyle,
} from '../mesh/meshBuilder';
import { SURFACE, edgeIdFor } from '../mesh/attributes';
import { layoutToWorld } from '../../core/coords';
import { WORLD } from '../../core/config';
import type { Bank } from './banks';
import { coveredSpans, openSpans, type Span } from './quaySpans';
import { TIMBER_V } from './timber';

/*
 * The Amsterdam juk: squared oak posts with planks behind them on the land side and a cap beam on
 * their heads, with no typological development over the centuries (Jayasena 2019, Graaf- en
 * modderwerk, pp. 292-293, read as HTML on its academia.edu page). Posts 18 cm square from the
 * revetment nearest 1538 in date (Dijkstraat, 1500-25, p. 156). That source gives no spacing, so the
 * spacing comes from the other dated revetments: 0.40-0.60 m (c. 1380), about 0.30 m (c. 1387), 30 cm
 * posts 20 cm apart (1387-1424) and 0.30-0.40 m (c. 1602). Only 1387-1424 is unambiguous centre to
 * centre, at 0.50 m, and the rest bracket it; Prins Hendrikkade 53's 0.90 m (c. 1456-69) is the
 * outlier. Cap beam 18 x 20 cm (c. 1387, p. 146). Analogue set and reasoning: docs/plans/streetscape.md
 * section 3.2.
 */
export const JUK = { postSize: 0.18, postSpacing: 0.5, beamDepth: 0.18, beamHeight: 0.2 } as const;

export const POST_EDGE_BASE = 30000;
export const BEAM_EDGE_BASE = 40000;

export type QuayTimberKind = 'quayPost';

// A post closer to a bank's end would stand in the post or the beam of the bank round the corner.
const END_CLEARANCE_M = 0.2;
// Two beams meet on a mitre unless it would reach out further than this many beam depths.
const MAX_MITRE = 2;
const FRAME: Frame = { origin: [0, 0, 0], yaw: 0 };

function isTimbered(bank: Bank): boolean {
  return bank.edge.quay === 'wood';
}

function towardWater(bank: Bank): Vec2 {
  return [bank.direction[1], -bank.direction[0]];
}

function pointAlong(bank: Bank, along: number): Vec2 {
  return [bank.a[0] + bank.direction[0] * along, bank.a[1] + bank.direction[1] * along];
}

function timberStyle(palette: PaletteModel, edgeId: number): VertexStyle {
  return {
    color: palette.shipHull.median.linear,
    surface: SURFACE.wood,
    edgeId,
    seed: 0,
    strokeAngle: 0,
    extent: [0, 0],
  };
}

/** Where along a bank its posts stand: every post spacing of its chain, clear of its ends and of decks. */
export function postPositions(bank: Bank, covered: readonly Span[]): number[] {
  const spacing = JUK.postSpacing;
  const half = JUK.postSize / 2;
  const out: number[] = [];
  for (
    let k = Math.ceil(bank.startU / spacing - 0.5);
    (k + 0.5) * spacing < bank.startU + bank.length;
    k++
  ) {
    const along = (k + 0.5) * spacing - bank.startU;
    const nearEnd = along < END_CLEARANCE_M || along > bank.length - END_CLEARANCE_M;
    const underDeck = covered.some(([from, to]) => along + half > from && along - half < to);
    if (!nearEnd && !underDeck) out.push(along);
  }
  return out;
}

// Columns: along the bank, up, toward the water, and the post's foot on the bank below the water.
function postMatrix(bank: Bank, along: number, heightAt: (p: Vec2) => number): readonly number[] {
  const [dx, dy] = bank.direction;
  const [nx, ny] = towardWater(bank);
  const [px, py] = pointAlong(bank, along);
  const w = JUK.postSize;
  const height = heightAt([px, py]) - JUK.beamHeight - WORLD.quayBottomY;
  return [
    dx * w,
    0,
    -dy * w,
    0,
    0,
    height,
    0,
    0,
    nx * w,
    0,
    -ny * w,
    0,
    px,
    WORLD.quayBottomY,
    -py,
    1,
  ];
}

export function quayPostSet(
  banks: readonly Bank[],
  decks: readonly PolygonWithHoles[],
  heightAt: (p: Vec2) => number = () => WORLD.groundY
): NearDetailSet<QuayTimberKind> {
  const placed = banks.filter(isTimbered).flatMap((bank) =>
    postPositions(bank, coveredSpans(bank, decks, JUK.beamDepth)).map((along) => ({
      bank,
      along,
    }))
  );
  const matrices = new Float32Array(placed.length * 16);
  const edgeIds = new Float32Array(placed.length);
  placed.forEach(({ bank, along }, i) => {
    matrices.set(postMatrix(bank, along, heightAt), i * 16);
    edgeIds[i] = edgeIdFor(POST_EDGE_BASE + bank.index);
  });
  return { kind: 'quayPost', count: placed.length, matrices, edgeIds };
}

/** A unit post: across x from -0.5 to 0.5, up y from 0 to 1, out z from the planks at 0 to the water at 1. */
export function quayPostTemplate(palette: PaletteModel): BufferGeometry {
  const builder = createMeshBuilder(16);
  const style = timberStyle(palette, edgeIdFor(POST_EDGE_BASE));
  const uv: readonly [Vec2, Vec2, Vec2, Vec2] = [
    [0, TIMBER_V],
    [1, TIMBER_V],
    [1, TIMBER_V],
    [0, TIMBER_V],
  ];
  builder.quad(
    style,
    FRAME,
    [
      [-0.5, 0, 1],
      [0.5, 0, 1],
      [0.5, 1, 1],
      [-0.5, 1, 1],
    ],
    uv
  );
  builder.quad(
    style,
    FRAME,
    [
      [0.5, 0, 1],
      [0.5, 0, 0],
      [0.5, 1, 0],
      [0.5, 1, 1],
    ],
    uv
  );
  builder.quad(
    style,
    FRAME,
    [
      [-0.5, 0, 0],
      [-0.5, 0, 1],
      [-0.5, 1, 1],
      [-0.5, 1, 0],
    ],
    uv
  );
  return builder.build();
}

/** Where the front faces of two beams meet round a joint of their banks, or null for square ends. */
function mitre(before: Bank, after: Bank): Vec2 | null {
  const [n1, n2] = [towardWater(before), towardWater(after)];
  const cos = n1[0] * n2[0] + n1[1] * n2[1];
  if (cos <= -0.5) return null;
  const scale = JUK.beamDepth / (1 + cos);
  const offset: Vec2 = [(n1[0] + n2[0]) * scale, (n1[1] + n2[1]) * scale];
  if (Math.hypot(offset[0], offset[1]) > MAX_MITRE * JUK.beamDepth) return null;
  return [before.b[0] + offset[0], before.b[1] + offset[1]];
}

interface Piece {
  readonly bank: Bank;
  readonly span: Span;
  readonly start: Vec2 | null;
  readonly end: Vec2 | null;
}

function piecesOf(banks: readonly Bank[], open: ReadonlyMap<number, readonly Span[]>): Piece[] {
  const reaches = (index: number | null, at: 'start' | 'end'): Bank | null => {
    const bank = index === null ? null : banks[index]!;
    const spans = bank ? open.get(bank.index) : undefined;
    if (!bank || !spans || spans.length === 0) return null;
    const whole = at === 'start' ? spans[0]![0] === 0 : spans[spans.length - 1]![1] === bank.length;
    return whole ? bank : null;
  };
  return banks.filter(isTimbered).flatMap((bank) =>
    (open.get(bank.index) ?? []).map((span): Piece => {
      const previous = span[0] === 0 ? reaches(bank.previous, 'end') : null;
      const next = span[1] === bank.length ? reaches(bank.next, 'start') : null;
      return {
        bank,
        span,
        start: previous ? mitre(previous, bank) : null,
        end: next ? mitre(bank, next) : null,
      };
    })
  );
}

function uvAt(bank: Bank, p: Vec2): Vec2 {
  const along = (p[0] - bank.a[0]) * bank.direction[0] + (p[1] - bank.a[1]) * bank.direction[1];
  return [bank.startU + along, TIMBER_V];
}

/*
 * Where the ground falls from the Dam to the quays, a bank's top is a curve, and a beam laid straight
 * from one end of the bank to the other hung in the air over its posts. A beam is therefore cut
 * wherever the bank's top leaves the straight line between two cuts by more than `toleranceM`, down
 * to lengths of `shortestM`: scarfed lengths of timber, as a long cap beam was made up of.
 */
const BEAM_FIT = { toleranceM: 0.01, shortestM: 1, checks: [0.25, 0.5, 0.75] } as const;

/** Where along its bank a beam is cut: its two ends, and every place between where its top bends. */
export function beamStations(bank: Bank, span: Span, heightAt: (p: Vec2) => number): number[] {
  const height = (along: number) => heightAt(pointAlong(bank, along));
  const cuts = (from: number, to: number): number[] => {
    if (to - from <= BEAM_FIT.shortestM) return [];
    const [h0, h1] = [height(from), height(to)];
    const bent = BEAM_FIT.checks.some(
      (t) => Math.abs(height(from + (to - from) * t) - (h0 + (h1 - h0) * t)) > BEAM_FIT.toleranceM
    );
    if (!bent) return [];
    const middle = (from + to) / 2;
    return [...cuts(from, middle), middle, ...cuts(middle, to)];
  };
  return [span[0], ...cuts(span[0], span[1]), span[1]];
}

interface BeamLength {
  readonly bank: Bank;
  /** Its back corners on the bank and its front corners over the water, at its two ends. */
  readonly back: readonly [Vec2, Vec2];
  readonly front: readonly [Vec2, Vec2];
  /** Whether each end is square: the beam neither runs on nor meets another on a mitre there. */
  readonly squareEnd: readonly [boolean, boolean];
}

// Top, front to the water, underside, and a square end wherever the beam does not meet another.
function buildBeamLength(
  { bank, back: [b0, b1], front: [f0, f1], squareEnd }: BeamLength,
  style: VertexStyle,
  builder: MeshBuilder,
  heightAt: (p: Vec2) => number
): void {
  const [top, bottom] = [WORLD.groundY, WORLD.groundY - JUK.beamHeight];
  const face = (corners: readonly (readonly [Vec2, number])[]) => {
    const points = corners.map(([p, y]) =>
      layoutToWorld(p, heightAt(p) + y - WORLD.groundY)
    ) as unknown as [Vec3, Vec3, Vec3, Vec3];
    const uv = corners.map(([p]) => uvAt(bank, p)) as unknown as [Vec2, Vec2, Vec2, Vec2];
    builder.quad(style, FRAME, points, uv);
  };
  face([
    [b0, top],
    [f0, top],
    [f1, top],
    [b1, top],
  ]);
  face([
    [f0, bottom],
    [f1, bottom],
    [f1, top],
    [f0, top],
  ]);
  face([
    [b0, bottom],
    [b1, bottom],
    [f1, bottom],
    [f0, bottom],
  ]);
  if (squareEnd[0])
    face([
      [b0, bottom],
      [f0, bottom],
      [f0, top],
      [b0, top],
    ]);
  if (squareEnd[1])
    face([
      [f1, bottom],
      [b1, bottom],
      [b1, top],
      [f1, top],
    ]);
}

function buildBeam(
  piece: Piece,
  style: VertexStyle,
  builder: MeshBuilder,
  heightAt: (p: Vec2) => number
): void {
  const { bank, span } = piece;
  const [nx, ny] = towardWater(bank);
  const stations = beamStations(bank, span, heightAt);
  const last = stations.length - 1;
  const back = stations.map((along) => pointAlong(bank, along));
  const front = back.map((p, i): Vec2 => {
    const mitred = i === 0 ? piece.start : i === last ? piece.end : null;
    return mitred ?? [p[0] + nx * JUK.beamDepth, p[1] + ny * JUK.beamDepth];
  });
  for (let i = 0; i < last; i++)
    buildBeamLength(
      {
        bank,
        back: [back[i]!, back[i + 1]!],
        front: [front[i]!, front[i + 1]!],
        squareEnd: [i === 0 && !piece.start, i === last - 1 && !piece.end],
      },
      style,
      builder,
      heightAt
    );
}

/**
 * The capping beam along every wooden bank, over the water and flush with the quay top, cut where a
 * bridge deck covers the bank and mitred where two wooden banks meet.
 */
export function buildCappingBeams(
  banks: readonly Bank[],
  decks: readonly PolygonWithHoles[],
  palette: PaletteModel,
  chunks: ChunkSink,
  heightAt: (p: Vec2) => number = () => WORLD.groundY
): { readonly pieces: number } {
  const open = new Map(
    banks
      .filter(isTimbered)
      .map(
        (bank) =>
          [bank.index, openSpans(bank.length, coveredSpans(bank, decks, JUK.beamDepth))] as const
      )
  );
  const pieces = piecesOf(banks, open);
  for (const piece of pieces) {
    const middle = pointAlong(piece.bank, (piece.span[0] + piece.span[1]) / 2);
    const style = timberStyle(palette, edgeIdFor(BEAM_EDGE_BASE + piece.bank.index));
    buildBeam(piece, style, chunks.builderAt('infrastructure', middle), heightAt);
  }
  return { pieces: pieces.length };
}
